import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { useSite } from '../context/SiteContext';
import { buildBrandOptions, normalizeBrandKey, normalizeBrandLabel } from '../utils/brandFilters';
import {
  TrendingUp, DollarSign, Calendar,
  BarChart3, PieChart, Info, Download, 
  Package, XCircle, RefreshCcw, Search,
  Calculator, Scale, Layers, Filter, Tag,
  ChevronDown, ChevronUp, CheckSquare, Square, Store
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  goToFirstSaleOnboardingStep,
  matchesFirstSaleOnboardingIdentity,
  readFirstSaleOnboarding,
} from '../utils/firstSaleOnboarding';
import { sanitizeProductDisplayName } from '../utils/productLabels';
import { fetchAllPaginated } from '../utils/productCatalog';
import { buildSaleAdjustmentFactors, saleAdjustmentFactor } from '../utils/reportesInteligentes';
import { calcularPuntoEquilibrio, incluyeGastosVariables, leyendaPuntoEquilibrio } from '../utils/puntoEquilibrio';

const RENTABILIDAD_COSTS_UPDATED_EVENT = 'impulza:product-costs-updated';
const RENTABILIDAD_COSTS_UPDATED_STORAGE_KEY = 'impulza:product-costs-updated:v1';

// Estados de ventas a excluir: 3 variantes de cancelado + presupuestos
const INVALID_SALE_STATUSES = ['canceled', 'cancelled', 'anulada', 'quote'];
const SALE_STATUS_NOT_IN = `(${INVALID_SALE_STATUSES.map((s) => `"${s}"`).join(',')})`;

// Movimiento de caja/banco anulado: Caja solo marca la descripción con
// "(ANULADO)" (no puede poner monto 0); Bancos además pone is_active=false.
const esMovimientoAnulado = (mov) =>
  mov?.is_active === false || String(mov?.descripcion || '').toUpperCase().includes('(ANULADO)');

/**
 * Rentabilidad de Ventas
 * - Ventas: Calculadas dinámicamente (Qty * Unit Price)
 * - CMV: Basado en costos de productos/variantes.
 * - Costos Fijos: Desde tabla Gastos
 * - Impuestos: Comisiones definidas en Métodos de Cobro
 * * REFACTORIZADO: Botón analizar en filtros, checklist categorías y ordenamiento columnas.
 * * ACTUALIZACIÓN: Desglose Fijo/Variable integrando Caja y Bancos.
 * * LÓGICOS DE EMPRESA (Config → Reportes): rentabilidad_incluye_productos
 *   (bloque Ventas/CMV/Margen Bruto) y rentabilidad_incluye_otros_ingresos
 *   (bloque servicios: ingresos + gastos de proyectos + margen por servicios).
 */
function RentabilidadVentas() {
  const navigate = useNavigate();
  const { profile, organization } = useAuth();
  // Lógicos de empresa que configuran el Estado de Resultados:
  // - Servicios: suma ingresos de tesorería (no ligados a ventas) y gastos de
  //   proyectos como bloque "por servicios". Default false.
  // - Productos: muestra Ventas Totales, CMV y Margen Bruto de productos.
  //   Default true (comportamiento histórico).
  const incluirServicios = Boolean(organization?.rentabilidad_incluye_otros_ingresos);
  const incluirProductos = organization
    ? organization.rentabilidad_incluye_productos !== false
    : true;
  // Punto de equilibrio: ¿los gastos "Variable" restan del margen? Default true.
  const peIncluyeVariables = incluyeGastosVariables(organization);
  // Mostrar la tab "Rentabilidad por producto" (listado por producto). Default true.
  const mostrarPorProducto = organization
    ? organization.rentabilidad_mostrar_por_producto !== false
    : true;
  // Sucursal activa (Topbar). Vacío = consolidado de toda la organización.
  const { selectedSiteId, sites, isMultiSite } = useSite();

  const orgId = profile?.organization_id;

  // Filtro de sucursal propio de la página: arranca sincronizado con la Topbar
  // pero el usuario puede cambiarlo localmente (vacío = consolidado).
  const [siteFilter, setSiteFilter] = useState(selectedSiteId || '');

  // Si cambia la sucursal global (Topbar), re-sincronizamos el filtro local.
  useEffect(() => {
    setSiteFilter(selectedSiteId || '');
  }, [selectedSiteId]);

  const [loading, setLoading] = useState(false);
  const [detalles, setDetalles] = useState([]);
  
  // Control de Vistas (Tabs)
  const [activeView, setActiveView] = useState('products'); // 'products' | 'results' | 'balance'

  // Resumen financiero
  const [summary, setSummary] = useState({ 
    ventas: 0, 
    cmv: 0, 
    beneficioBruto: 0, 
    margenBrutoPct: 0 
  });
  
  const [gastosFijos, setGastosFijos] = useState(0);
  // NUEVO: Estado para gastos variables
  const [gastosVariables, setGastosVariables] = useState(0); 
  const [impuestosComisiones, setImpuestosComisiones] = useState(0);
  // Bloque "por servicios" (solo con el lógico de servicios activo):
  // - Ingresos por servicios: ingresos de tesorería no ligados a ventas.
  // - Gastos por servicios: gastos de proyectos (crm_project_expenses).
  const [otrosIngresos, setOtrosIngresos] = useState(0);
  const [rawOtrosIngresosDetail, setRawOtrosIngresosDetail] = useState([]);
  const [gastosServicios, setGastosServicios] = useState(0);
  const [rawGastosServiciosDetail, setRawGastosServiciosDetail] = useState([]);

  // --- CAMBIO: Fechas Default: Hoy a Hoy (antes era mes completo) ---
  const [fechaInicio, setFechaInicio] = useState(new Date().toLocaleDateString('en-CA'));
  const [fechaFin, setFechaFin] = useState(new Date().toLocaleDateString('en-CA'));

  const [searchTerm, setSearchTerm] = useState('');
  const [brandFilter, setBrandFilter] = useState('');

  // --- NUEVO: Estados para Filtro de Categorías y Ordenamiento ---
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]); // IDs seleccionados
  const [showCategoryMenu, setShowCategoryMenu] = useState(false);
  const categoryMenuRef = useRef(null);

  const [sortConfig, setSortConfig] = useState({ key: 'venta', direction: 'desc' }); // Default: Mayor venta primero
  const [firstSaleJourney, setFirstSaleJourney] = useState(null);

  // Detalle de movimientos por punto del Estado de Resultados / Punto de Equilibrio
  const [rawSalesDetail, setRawSalesDetail] = useState([]);
  const [rawGastosFijosDetail, setRawGastosFijosDetail] = useState([]);
  const [rawGastosVariablesDetail, setRawGastosVariablesDetail] = useState([]);
  const [selectedDetail, setSelectedDetail] = useState(null); // { type, label, total }

  useEffect(() => {
    if (orgId) {
      fetchCategories();
      analizarPeriodo();
    }
    // siteFilter: re-analiza al cambiar de sucursal (Topbar o selector local).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, siteFilter]);

  useEffect(() => {
    if (!orgId) return undefined;

    const handleCostsUpdated = () => {
      analizarPeriodo();
    };

    const handleStorage = (event) => {
      if (event.key && event.key !== RENTABILIDAD_COSTS_UPDATED_STORAGE_KEY) return;
      analizarPeriodo();
    };

    window.addEventListener(RENTABILIDAD_COSTS_UPDATED_EVENT, handleCostsUpdated);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(RENTABILIDAD_COSTS_UPDATED_EVENT, handleCostsUpdated);
      window.removeEventListener('storage', handleStorage);
    };
  }, [orgId, fechaInicio, fechaFin, siteFilter]);

  // Cerrar menú de categorías al hacer click afuera
  useEffect(() => {
    function handleClickOutside(event) {
      if (categoryMenuRef.current && !categoryMenuRef.current.contains(event.target)) {
        setShowCategoryMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!orgId) return;

    const onboardingState = readFirstSaleOnboarding();
    const isJourneyActive =
      matchesFirstSaleOnboardingIdentity(onboardingState, {
        organizationId: orgId,
        userId: profile?.id || null,
      }) &&
      onboardingState?.stepId === 'review-profitability';

    if (!isJourneyActive) {
      setFirstSaleJourney(null);
      return;
    }

    setFirstSaleJourney(onboardingState);
    setActiveView('results');

    if (onboardingState?.sale?.productName) {
      setSearchTerm(onboardingState.sale.productName);
    }

    if (onboardingState?.sale?.date) {
      setFechaInicio(onboardingState.sale.date);
      setFechaFin(onboardingState.sale.date);
    }
  }, [orgId, profile?.id]);

  useEffect(() => {
    if (!firstSaleJourney?.sale?.date) return;
    if (fechaInicio !== firstSaleJourney.sale.date || fechaFin !== firstSaleJourney.sale.date) return;
    analizarPeriodo();
  }, [firstSaleJourney, fechaInicio, fechaFin]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('product_categories')
        .select('id, name')
        .eq('organization_id', orgId)
        .eq('is_active', true)
        .order('name');
      
      if (error) throw error;
      setCategories(data || []);
      // Por defecto seleccionamos todas? O ninguna (significa todas). Vamos con vacio = todas.
    } catch (err) {
      console.error('Error fetching categories', err);
    }
  };

  const money = (n) =>
    Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const brandOptions = useMemo(() => buildBrandOptions(detalles, (detalle) => detalle?.brand), [detalles]);

  // Guard contra ejecuciones duplicadas (mismo orgId+fechas en <500ms)
  // Evita que multiple useEffects disparen el análisis a la vez.
  const analizarPeriodoLastKeyRef = useRef({ key: null, at: 0 });

  const analizarPeriodo = async () => {
    if (!orgId) return;
    const key = `${orgId}|${siteFilter || 'all'}|${fechaInicio}|${fechaFin}|${selectedCategories.join(',')}`;
    const now = Date.now();
    if (analizarPeriodoLastKeyRef.current.key === key && now - analizarPeriodoLastKeyRef.current.at < 500) {
      return; // misma combinación corrió hace <500ms, saltar
    }
    analizarPeriodoLastKeyRef.current = { key, at: now };
    setLoading(true);

    try {
      const startIso = `${fechaInicio}T00:00:00`;
      const endIso = `${fechaFin}T23:59:59`;

      // 1. OBTENER VENTAS CABECERA + MÉTODOS DE COBRO
      // Traemos payment_method_id explícitamente para hacer el join robusto por separado.
      const salesHeaderData = await fetchAllPaginated(() => {
        let q = supabase
          .from('sales')
          .select(`
            id,
            total,
            created_at,
            payment_method_id,
            payment_methods (name, commission_percentage)
          `)
          .eq('organization_id', orgId)
          .gte('created_at', startIso)
          .lte('created_at', endIso)
          .not('status', 'in', SALE_STATUS_NOT_IN);
        // Filtro por sucursal (vacío = consolidado).
        if (siteFilter) q = q.eq('site_id', siteFilter);
        return q;
      });

      // Si el join de Supabase no trajo commission_percentage, hacemos lookup manual
      const missingMethodIds = [...new Set(
        (salesHeaderData || [])
          .filter(s => s.payment_method_id && s.payment_methods?.commission_percentage == null)
          .map(s => s.payment_method_id)
      )];

      const methodCommissionMap = new Map();
      // Primero, poblar desde el join
      (salesHeaderData || []).forEach(s => {
        if (s.payment_method_id && s.payment_methods?.commission_percentage != null) {
          methodCommissionMap.set(s.payment_method_id, Number(s.payment_methods.commission_percentage || 0));
        }
      });

      // Si faltan, buscarlos explícitamente
      if (missingMethodIds.length > 0) {
        const { data: methodsData } = await supabase
          .from('payment_methods')
          .select('id, commission_percentage')
          .in('id', missingMethodIds);
        (methodsData || []).forEach(m => {
          methodCommissionMap.set(m.id, Number(m.commission_percentage || 0));
        });
      }

      const totalComisiones = (salesHeaderData || []).reduce((acc, sale) => {
        const pct = sale.payment_method_id
          ? (methodCommissionMap.get(sale.payment_method_id) ?? Number(sale.payment_methods?.commission_percentage || 0))
          : Number(sale.payment_methods?.commission_percentage || 0);
        const montoComision = (Number(sale.total || 0) * pct) / 100;
        return acc + montoComision;
      }, 0);
      
      const realTotalVentas = (salesHeaderData || []).reduce((acc, sale) => acc + (Number(sale.total) || 0), 0);

      setImpuestosComisiones(totalComisiones);

      // 2. OBTENER GASTOS (LOGICA ACTUALIZADA FIJO/VARIABLE)
      // a) Tabla Gastos (Registro manual formal)
      const gastosData = await fetchAllPaginated(() => {
        let q = supabase
          .from('gastos')
          .select('monto, categoria, concepto, id, fecha') // Traemos ID para evitar duplicados si quisieramos filtrar estricto
          .eq('organization_id', orgId)
          .gte('fecha', fechaInicio)
          .lte('fecha', fechaFin)
          .eq('is_active', true);
        // Sucursal: filtro estricto. La rentabilidad por sucursal solo cuenta los
        // gastos asignados a esa sucursal (los compartidos/sin sucursal se excluyen).
        if (siteFilter) q = q.eq('sitio_id', siteFilter);
        return q;
      });

      // b) Tabla Movimientos Caja (Gastos rápidos de caja)
      const cajaData = await fetchAllPaginated(() => {
        let q = supabase
          .from('movimientos_caja')
          .select('monto, categoria, descripcion, fecha')
          .eq('organization_id', orgId)
          .eq('tipo', 'Gasto')
          .gte('fecha', startIso)
          .lte('fecha', endIso);
        if (siteFilter) q = q.eq('sitio_id', siteFilter);
        return q;
      });

      // c) Tabla Movimientos Financieros (Egresos de banco/billeteras)
      const bancoData = await fetchAllPaginated(() => {
        let q = supabase
          .from('movimientos_financieros')
          .select('monto, categoria, descripcion, fecha_operacion, is_active')
          .eq('organization_id', orgId)
          .eq('tipo', 'Egreso')
          .gte('fecha_operacion', startIso)
          .lte('fecha_operacion', endIso);
        if (siteFilter) q = q.eq('sitio_id', siteFilter);
        return q;
      });

      // -- DETALLE DE VENTAS (para recuadro Ventas Totales / Impuestos) --
      const salesDetail = (salesHeaderData || []).map((s) => {
        const pct = s.payment_method_id
          ? (methodCommissionMap.get(s.payment_method_id) ?? Number(s.payment_methods?.commission_percentage || 0))
          : Number(s.payment_methods?.commission_percentage || 0);
        const total = Number(s.total || 0);
        return {
          id: s.id,
          total,
          fecha: s.created_at,
          paymentMethod: s.payment_methods?.name || 'Sin metodo',
          commissionPct: pct,
          commission: (total * pct) / 100,
        };
      });
      setRawSalesDetail(salesDetail);

      // -- PROCESAMIENTO UNIFICADO DE GASTOS --
      let acumFijo = 0;
      let acumVariable = 0;
      const fijosDetail = [];
      const variablesDetail = [];
      const pushDetail = (item) => {
        const c = (item.categoria || '').toLowerCase();
        if (c.includes('fijo')) fijosDetail.push(item);
        else variablesDetail.push(item);
      };
      
      // Usamos un Set para intentar no duplicar si un gasto de la tabla 'gastos' generó un movimiento en 'caja'
      // Esta es una heurística simple: Sumamos todo, pero ten en cuenta que si registras en ambos lados manualmente se duplicará.
      // El sistema Gastos.jsx actual inserta en ambos. 
      // Para efectos de rentabilidad, priorizamos la TABLA GASTOS + los movimientos de caja/banco que NO tengan origen en gastos.
      // Como es difícil saber el origen exacto sin un ID de enlace, sumaremos TODO lo de Gastos, 
      // y sumaremos de Caja/Banco solo lo que NO parezca venir del módulo de gastos (heurística por descripción).
      // POR AHORA: Sumamos todo como solicitaste ("que los gastos registrados en arqueo vayan a gastos").
      
      const procesarMonto = (monto, categoria) => {
          const m = Number(monto || 0);
          const c = (categoria || '').toLowerCase();
          if (c.includes('fijo')) {
              acumFijo += m;
          } else {
              // Si es 'Variable', 'Varios', 'Envio', etc. va a variable
              acumVariable += m;
          }
      };

      // 1. Tabla Gastos (Prioridad)
      (gastosData || []).forEach(g => {
        const cat = g.categoria || 'Variable';
        procesarMonto(g.monto, cat);
        pushDetail({
          source: 'Gastos',
          fecha: g.fecha,
          descripcion: g.concepto || 'Sin concepto',
          categoria: cat,
          monto: Number(g.monto || 0),
        });
      });

      // 2. Caja
      // Mismo criterio que Banco (abajo): 'Compras' en caja es el pago contado
      // de una compra de mercadería; ese costo ya entra por CMV.
      const NON_EXPENSE_CASH_CATEGORIES = new Set(['compras', 'pago', 'transferencia']);
      (cajaData || []).forEach(c => {
          if (NON_EXPENSE_CASH_CATEGORIES.has(String(c.categoria || '').toLowerCase().trim())) return;
          // Anular en Caja solo antepone "(ANULADO)" a la descripción (la base
          // no admite monto 0): ese movimiento no es gasto.
          if (esMovimientoAnulado(c)) return;
          const cDesc = (c.descripcion || '').toUpperCase();
          const isDuplicate = gastosData?.some(g => {
              const gConc = (g.concepto || '').toUpperCase();
              return Math.abs(g.monto - c.monto) < 0.1 && (gConc.includes(cDesc) || cDesc.includes(gConc));
          });
          if (!isDuplicate) {
             const cat = c.categoria || 'Variable';
             procesarMonto(c.monto, cat);
             pushDetail({
               source: 'Caja',
               fecha: c.fecha,
               descripcion: c.descripcion || 'Movimiento de caja',
               categoria: cat,
               monto: Number(c.monto || 0),
             });
          }
      });

      // 3. Banco
      // Excluimos egresos que NO son gasto operativo del período, para no inflar el Estado
      // de Resultados ni duplicar el CMV:
      //  - 'Compras': la mercadería ya se computa por CMV (costo de lo vendido).
      //  - 'Pago': pagos a proveedores; cancelan deuda de cuenta corriente, no son gasto del período.
      //  - 'Transferencia' / 'Ajuste Banco': movimientos internos de tesorería.
      const NON_EXPENSE_BANK_CATEGORIES = new Set(['compras', 'pago', 'transferencia', 'ajuste banco']);
      (bancoData || []).forEach(b => {
          if (NON_EXPENSE_BANK_CATEGORIES.has(String(b.categoria || '').toLowerCase().trim())) return;
          if (esMovimientoAnulado(b)) return;
          const bDesc = (b.descripcion || '').toUpperCase();
          const isDuplicate = gastosData?.some(g => {
              const gConc = (g.concepto || '').toUpperCase();
              return Math.abs(g.monto - b.monto) < 0.1 && (gConc.includes(bDesc) || bDesc.includes(gConc));
          });
          if (!isDuplicate) {
             const cat = b.categoria || 'Variable';
             procesarMonto(b.monto, cat);
             pushDetail({
               source: 'Banco',
               fecha: b.fecha_operacion,
               descripcion: b.descripcion || 'Egreso financiero',
               categoria: cat,
               monto: Number(b.monto || 0),
             });
          }
      });

      // Actualizamos estados
      // const totalGastos = (gastosData || []).reduce((acc, g) => acc + Number(g.monto || 0), 0); // ANTERIOR
      setGastosFijos(acumFijo);
      setGastosVariables(acumVariable);
      setRawGastosFijosDetail(fijosDetail);
      setRawGastosVariablesDetail(variablesDetail);

      // 2.b OTROS INGRESOS (solo si el lógico de empresa está activo)
      // Ingresos de tesorería NO ligados a una venta. Regla anti-doble-conteo:
      // se toman únicamente los movimientos con documento_tipo NULL. Eso excluye
      // los cobros de ventas (documento_tipo 'Venta'/'Venta Online'/'sale', ya
      // contados desde `sales`) y los técnicos de banco ('Ajuste Banco',
      // 'Transferencia'). Respeta rango de fechas (fecha_operacion) y sucursal
      // (sitio_id), igual que el resto del reporte.
      if (incluirServicios) {
        const otrosIngresosData = await fetchAllPaginated(() => {
          let q = supabase
            .from('movimientos_financieros')
            .select('monto, categoria, descripcion, fecha_operacion')
            .eq('organization_id', orgId)
            .eq('tipo', 'Ingreso')
            .eq('is_active', true)
            .is('documento_tipo', null)
            .gte('fecha_operacion', startIso)
            .lte('fecha_operacion', endIso);
          if (siteFilter) q = q.eq('sitio_id', siteFilter);
          return q;
        });
        const totalOtros = (otrosIngresosData || []).reduce(
          (acc, m) => acc + Number(m.monto || 0),
          0,
        );
        setOtrosIngresos(totalOtros);
        setRawOtrosIngresosDetail(
          (otrosIngresosData || []).map((m) => ({
            fecha: m.fecha_operacion,
            categoria: m.categoria || 'Ingreso',
            descripcion: m.descripcion || 'Ingreso de servicios',
            monto: Number(m.monto || 0),
          })),
        );

        // Gastos por servicios = gastos de proyectos (crm_project_expenses).
        // Viven en su propia tabla; NO se insertan en `gastos` ni en
        // `movimientos_financieros`, así que no se duplican con "otros gastos".
        // Se incluyen todos (absorbidos y refacturados al cliente). Sin dimensión
        // de sucursal en la tabla, se toman a nivel organización.
        const gastosProyData = await fetchAllPaginated(() =>
          supabase
            .from('crm_project_expenses')
            .select('amount, category, description, expense_date')
            .eq('organization_id', orgId)
            .gte('expense_date', fechaInicio)
            .lte('expense_date', fechaFin),
        );
        const totalGastosProy = (gastosProyData || []).reduce(
          (acc, g) => acc + Number(g.amount || 0),
          0,
        );
        setGastosServicios(totalGastosProy);
        setRawGastosServiciosDetail(
          (gastosProyData || []).map((g) => ({
            fecha: g.expense_date,
            categoria: g.category || 'Proyecto',
            descripcion: g.description || 'Gasto de proyecto',
            monto: Number(g.amount || 0),
          })),
        );
      } else {
        setOtrosIngresos(0);
        setRawOtrosIngresosDetail([]);
        setGastosServicios(0);
        setRawGastosServiciosDetail([]);
      }

      // 3. OBTENER ITEMS VENDIDOS
      // CAMBIO: Agregamos category_id al select del producto
      // Para ventas de promo/combo, sale_items guarda 1 fila parent (resumen, sin
      // costo) + N filas component (ingredientes con costo real y revenue prorrateado).
      // Filtramos las filas parent para que la rentabilidad cuente sólo componentes
      // y productos sueltos. Los servidores antiguos sin la columna devuelven NULL,
      // así que el filtro neq también permite NULL implícitamente (los productos
      // sueltos tienen combo_role NULL).
      const salesItemsData = await fetchAllPaginated(() => {
        let q = supabase
          .from('sale_items')
          .select(`
            sale_id,
            quantity,
            unit_price,
            subtotal,
            variant_id,
            product_name_snapshot,
            combo_role,
            producto:products!vinc_prod (id, name, brand, cost_price, category_id),
            venta:sales!vinc_sale!inner (id, total, created_at, organization_id, status, site_id)
          `)
          .eq('venta.organization_id', orgId)
          .gte('venta.created_at', startIso)
          .lte('venta.created_at', endIso)
          .not('venta.status', 'in', SALE_STATUS_NOT_IN);
        // Sucursal: filtramos por el site_id de la venta padre.
        if (siteFilter) q = q.eq('venta.site_id', siteFilter);
        return q;
      });

      if (!salesItemsData || salesItemsData.length === 0) {
        setDetalles([]);
        // NOTA: No reseteamos impuestosComisiones aquí porque ya fue calculado correctamente
        // con los datos de salesHeaderData. Solo reseteamos el CMV y el detalle de productos.
        setSummary({ ventas: realTotalVentas, cmv: 0, beneficioBruto: realTotalVentas, margenBrutoPct: 100 });
        return;
      }

      // --- RESOLUCIÓN DE COSTOS UNITARIOS ---
      const pIds = [...new Set(salesItemsData.map((item) => item.producto?.id).filter(Boolean))];

      const [vCosts, pCosts] = await Promise.all([
        fetchAllPaginated(() =>
          supabase.from('product_variants').select('id, product_id, cost_price').eq('organization_id', orgId).in('product_id', pIds)
        ),
        fetchAllPaginated(() =>
          supabase.from('products').select('id, cost_price').eq('organization_id', orgId).in('id', pIds)
        )
      ]);

      // Dos índices: por id de variante (ítem vendido con variante) y por
      // producto (ítem sin variante: primera variante con costo > 0). Antes
      // había un solo mapa indexado por product_id que se consultaba con el
      // variant_id → nunca matcheaba y toda venta con talle/color tomaba el
      // costo del producto en vez del de su variante.
      const variantCostById = new Map();
      const variantCostByProduct = new Map();
      (vCosts || []).forEach((r) => {
        const c = Number(r.cost_price || 0);
        if (r.id) variantCostById.set(r.id, c);
        if (c > 0 && !variantCostByProduct.has(r.product_id)) variantCostByProduct.set(r.product_id, c);
      });

      const productCostMap = new Map();
      (pCosts || []).forEach((r) => productCostMap.set(r.id, Number(r.cost_price || 0)));

      const resolveCost = (productId, variantId, productInlineCost) => {
        if (variantId) {
            const fromVariant = Number(variantCostById.get(variantId) || 0);
            if (fromVariant > 0) return fromVariant;
        } else {
            const fromVariantProductLevel = Number(variantCostByProduct.get(productId) || 0);
            if (fromVariantProductLevel > 0) return fromVariantProductLevel;
        }
        
        const fromProductQuery = Number(productCostMap.get(productId) || 0);
        if (fromProductQuery > 0) return fromProductQuery;
        const inline = Number(productInlineCost || 0);
        return inline > 0 ? inline : 0;
      };

      // --- AGRUPACIÓN Y CÁLCULOS ---
      const res = {};
      let totalVentasCalculado = 0;
      let totalCostoMercaderia = 0;

      // Descuento / recargo de cada venta repartido entre sus ítems (ver
      // buildSaleAdjustmentFactors): sin esto la venta por producto es precio
      // de lista y no cuadra con Ventas Totales.
      const saleFactors = buildSaleAdjustmentFactors(salesItemsData);

      salesItemsData.forEach((item) => {
        const p = item.producto;
        if (!p?.id) return;

        // Saltar las filas "parent" de combos: el revenue y costo reales viven
        // en las filas component. Si las sumáramos también, contaríamos doble la venta.
        if (item.combo_role === 'parent') return;

        const qty = Number(item.quantity || 0);
        let ventaItem = Number(item.subtotal || 0);
        if (ventaItem === 0) {
            ventaItem = qty * Number(item.unit_price || 0);
        }
        ventaItem *= saleAdjustmentFactor(saleFactors, item);

        const costoU = resolveCost(p.id, item.variant_id, p.cost_price);

        // Agrupar por ID de producto + ID de variante (si existe) para desglosar correctamente.
        const groupKey = item.variant_id ? `${p.id}-${item.variant_id}` : p.id;
        // Solo mostramos el snapshot si existe algo util y se diferencia del original.
        const displayName = sanitizeProductDisplayName(item.product_name_snapshot || p.name || 'Sin nombre', 'Sin nombre');

        if (!res[groupKey]) {
          res[groupKey] = {
            product_id: p.id,
            variant_id: item.variant_id,
            nombre: displayName,
            brand: normalizeBrandLabel(p.brand),
            brandKey: normalizeBrandKey(p.brand),
            category_id: p.category_id, // Guardamos la categoria para filtrar despues
            cant: 0,
            venta: 0,
            costoUnit: costoU, 
            cmv: 0
          };
        }

        res[groupKey].cant += qty;
        res[groupKey].venta += ventaItem;
        
        if (res[groupKey].costoUnit <= 0 && costoU > 0) res[groupKey].costoUnit = costoU;

        const costoTotalItem = qty * costoU;
        res[groupKey].cmv += costoTotalItem;

        totalVentasCalculado += ventaItem;
        totalCostoMercaderia += costoTotalItem;
      });

      const listado = Object.values(res).map((d) => {
          const precioProm = d.cant > 0 ? d.venta / d.cant : 0;
          const beneficio = d.venta - d.cmv;
          const margen = d.venta > 0 ? (beneficio / d.venta) * 100 : 0;
          return { ...d, precioProm, beneficio, margen };
        }); 
        // El sort inicial lo manejaremos con el useMemo y sortConfig
        // .sort((a, b) => b.venta - a.venta);

      setDetalles(listado);
      setSummary({
        ventas: realTotalVentas,
        cmv: totalCostoMercaderia,
        beneficioBruto: realTotalVentas - totalCostoMercaderia,
        margenBrutoPct: realTotalVentas > 0 ? ((realTotalVentas - totalCostoMercaderia) / realTotalVentas) * 100 : 0
      });

    } catch (e) {
      console.error('[RentabilidadVentas] Error:', e);
      toast.error('Error al calcular rentabilidad');
      setDetalles([]);
      setSummary({ ventas: 0, cmv: 0, beneficioBruto: 0, margenBrutoPct: 0 });
      setRawSalesDetail([]);
      setRawGastosFijosDetail([]);
      setRawGastosVariablesDetail([]);
      setOtrosIngresos(0);
      setRawOtrosIngresosDetail([]);
      setGastosServicios(0);
      setRawGastosServiciosDetail([]);
    } finally {
      setLoading(false);
    }
  };

  // --- LÓGICA DE FILTRADO Y ORDENAMIENTO REVISADA ---
  const filteredDetalles = useMemo(() => {
    // 1. Filtro por Buscador
    let data = detalles;
    const q = searchTerm.trim().toLowerCase();
    if (q) {
        data = data.filter((d) =>
          (d.nombre || '').toLowerCase().includes(q) ||
          normalizeBrandLabel(d.brand).toLowerCase().includes(q)
        );
    }

    // 2. Filtro por Categoría
    if (selectedCategories.length > 0) {
        data = data.filter(d => selectedCategories.includes(d.category_id));
    }

    if (brandFilter) {
        data = data.filter((d) => d.brandKey === brandFilter);
    }

    // 3. Ordenamiento
    if (sortConfig.key) {
        data = [...data].sort((a, b) => {
            const aVal = a[sortConfig.key];
            const bVal = b[sortConfig.key];
            
            // Manejo de strings vs numeros
            if (typeof aVal === 'string') {
                 if (aVal.toLowerCase() < bVal.toLowerCase()) return sortConfig.direction === 'asc' ? -1 : 1;
                 if (aVal.toLowerCase() > bVal.toLowerCase()) return sortConfig.direction === 'asc' ? 1 : -1;
                 return 0;
            } else {
                 // Numeros
                 if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                 if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                 return 0;
            }
        });
    }

    return data;
  }, [detalles, searchTerm, brandFilter, selectedCategories, sortConfig]);

  const isFiltered = Boolean(searchTerm.trim() || brandFilter || selectedCategories.length > 0);

  // Bloque servicios aplicado: solo con el lógico de servicios activo y SIN
  // filtro de producto (al filtrar por producto los totales se recalculan por
  // producto y el bloque servicios —sin producto— no corresponde). CB-2.
  const otrosIngresosAplicado = (incluirServicios && !isFiltered) ? otrosIngresos : 0;
  const gastosServiciosAplicado = (incluirServicios && !isFiltered) ? gastosServicios : 0;
  const margenServicios = otrosIngresosAplicado - gastosServiciosAplicado;

  const displayedKpis = useMemo(() => {
    if (!isFiltered) {
      return summary;
    }

    let totalVentas = 0;
    let totalCmv = 0;
    filteredDetalles.forEach((d) => {
      totalVentas += d.venta || 0;
      totalCmv += d.cmv || 0;
    });

    const beneficioBruto = totalVentas - totalCmv;
    const margenBrutoPct = totalVentas > 0 ? (beneficioBruto / totalVentas) * 100 : 0;

    return {
      ventas: totalVentas,
      cmv: totalCmv,
      beneficioBruto,
      margenBrutoPct,
    };
  }, [isFiltered, summary, filteredDetalles]);

  // Manejador de Ordenamiento
  const handleSort = (key) => {
    setSortConfig((prev) => {
        if (prev.key === key) {
            // Si ya estaba en esta key, invertimos.
            return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
        }
        // Nueva key, por defecto descendente para numeros (ventas, beneficio) o ascendente para texto?
        // Vamos a standarizar: primer click ascendente, segundo descendente, salvo que sea monto
        const defaultDescKeys = ['venta', 'cmv', 'beneficio', 'margen', 'cant'];
        const dir = defaultDescKeys.includes(key) ? 'desc' : 'asc';
        return { key, direction: dir };
    });
  };

  // Renderizador de Header Ordenable
  const SortableTh = ({ label, sortKey, align = 'left' }) => {
    const isActive = sortConfig.key === sortKey;
    return (
        <th 
            className={`px-6 py-4 cursor-pointer hover:bg-slate-100 transition-colors select-none ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'}`}
            onClick={() => handleSort(sortKey)}
        >
            <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start'}`}>
                {label}
                <div className="flex flex-col">
                    {isActive && sortConfig.direction === 'asc' && <ChevronUp size={10} className="text-[#51A7FD]" />}
                    {isActive && sortConfig.direction === 'desc' && <ChevronDown size={10} className="text-[#51A7FD]" />}
                    {!isActive && <div className="h-2.5 w-2.5 opacity-0 group-hover:opacity-30"><Filter size={10} /></div>}
                </div>
            </div>
        </th>
    );
  };

  // Toggle Categoría
  const toggleCategory = (catId) => {
    setSelectedCategories(prev => {
        if (prev.includes(catId)) return prev.filter(id => id !== catId);
        return [...prev, catId];
    });
  };

  const exportCSV = () => {
    const rows = filteredDetalles.map((d) => ({
      PRODUCTO: d.nombre,
      MARCA: normalizeBrandLabel(d.brand),
      CANTIDAD: d.cant,
      PRECIO_PROM: Number(d.precioProm || 0),
      COSTO_UNIT: Number(d.costoUnit || 0),
      VENTA_TOTAL: Number(d.venta || 0),
      COSTO_TOTAL: Number(d.cmv || 0),
      BENEFICIO: Number(d.beneficio || 0),
      MARGEN_PCT: Number(d.margen || 0)
    }));

    if (!rows.length) return toast.error('No hay datos para exportar');

    const header = Object.keys(rows[0]).join(',');
    const body = rows.map((o) => Object.values(o).join(',')).join('\n');
    const csv = `${header}\n${body}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Impulza_Rentabilidad.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exportado');
  };

  const pillClass = (margen) => {
    if (margen < 0) return 'bg-rose-50 text-rose-700 border-rose-100';
    if (margen < 20) return 'bg-amber-50 text-amber-700 border-amber-100';
    return 'bg-emerald-50 text-emerald-700 border-emerald-100';
  };

  // --- DETALLE DE MOVIMIENTOS POR PUNTO ---
  const formatDate = (d) => {
    if (!d) return '-';
    try {
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return String(d);
      return dt.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
    } catch {
      return String(d);
    }
  };

  const handleSelectDetail = (type, label) => {
    setSelectedDetail((prev) => (prev?.type === type ? null : { type, label }));
  };

  const currentDetailTotal = useMemo(() => {
    if (!selectedDetail) return 0;
    switch (selectedDetail.type) {
      case 'ventas': return displayedKpis.ventas;
      case 'cmv': return displayedKpis.cmv;
      case 'gastosVariables': return gastosVariables;
      case 'gastosFijos': return gastosFijos;
      case 'impuestos': return impuestosComisiones;
      case 'margenProductos': return displayedKpis.beneficioBruto;
      case 'otrosIngresos': return otrosIngresosAplicado;
      case 'gastosServicios': return gastosServiciosAplicado;
      default: return 0;
    }
  }, [selectedDetail, displayedKpis, gastosVariables, gastosFijos, impuestosComisiones, otrosIngresosAplicado, gastosServiciosAplicado]);

  // Reset al cambiar de tab
  useEffect(() => {
    setSelectedDetail(null);
  }, [activeView]);

  // Si la empresa apaga "Rentabilidad por producto" y la vista activa era esa,
  // la mandamos al Resultado operativo para no dejar una tab oculta seleccionada.
  useEffect(() => {
    if (!mostrarPorProducto && activeView === 'products') {
      setActiveView('results');
    }
  }, [mostrarPorProducto, activeView]);

  const detailRows = useMemo(() => {
    if (!selectedDetail) return [];
    switch (selectedDetail.type) {
      case 'ventas':
        return rawSalesDetail.map((s) => ({
          left: formatDate(s.fecha),
          mid: s.paymentMethod,
          right: `$ ${money(s.total)}`,
        }));
      case 'impuestos':
        return rawSalesDetail
          .filter((s) => s.commission > 0)
          .map((s) => ({
            left: formatDate(s.fecha),
            mid: `${s.paymentMethod} (${Number(s.commissionPct || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%)`,
            right: `$ ${money(s.commission)}`,
          }));
      case 'cmv':
        return [...detalles]
          .filter((d) => d.cmv > 0)
          .sort((a, b) => b.cmv - a.cmv)
          .map((d) => ({
            left: `x${Number(d.cant || 0).toLocaleString('es-AR')}`,
            mid: d.nombre,
            right: `$ ${money(d.cmv)}`,
          }));
      case 'gastosVariables':
        return [...rawGastosVariablesDetail]
          .sort((a, b) => (a.fecha < b.fecha ? 1 : -1))
          .map((g) => ({
            left: formatDate(g.fecha),
            mid: `${g.descripcion} · ${g.source}`,
            right: `$ ${money(g.monto)}`,
          }));
      case 'gastosFijos':
        return [...rawGastosFijosDetail]
          .sort((a, b) => (a.fecha < b.fecha ? 1 : -1))
          .map((g) => ({
            left: formatDate(g.fecha),
            mid: `${g.descripcion} · ${g.source}`,
            right: `$ ${money(g.monto)}`,
          }));
      case 'margenProductos':
        return [...detalles]
          .sort((a, b) => b.beneficio - a.beneficio)
          .map((d) => ({
            left: `${Number(d.margen || 0).toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`,
            mid: d.nombre,
            right: `$ ${money(d.beneficio)}`,
          }));
      case 'otrosIngresos':
        return [...rawOtrosIngresosDetail]
          .sort((a, b) => (a.fecha < b.fecha ? 1 : -1))
          .map((m) => ({
            left: formatDate(m.fecha),
            mid: `${m.descripcion} · ${m.categoria}`,
            right: `$ ${money(m.monto)}`,
          }));
      case 'gastosServicios':
        return [...rawGastosServiciosDetail]
          .sort((a, b) => (a.fecha < b.fecha ? 1 : -1))
          .map((g) => ({
            left: formatDate(g.fecha),
            mid: `${g.descripcion} · ${g.categoria}`,
            right: `$ ${money(g.monto)}`,
          }));
      default:
        return [];
    }
  }, [selectedDetail, rawSalesDetail, rawGastosFijosDetail, rawGastosVariablesDetail, detalles, rawOtrosIngresosDetail, rawGastosServiciosDetail]);

  // --- CÁLCULOS FINALES ---
  // El bloque de productos (Ventas, CMV, Beneficio Bruto) solo cuenta si el
  // lógico de productos está activo; si no, se excluye del resultado.
  const ventasAplicado = incluirProductos ? displayedKpis.ventas : 0;
  const beneficioBrutoAplicado = incluirProductos ? displayedKpis.beneficioBruto : 0;

  // Margen Operativo = Beneficio Bruto (productos) + Margen por servicios
  //                    - (Gastos Fijos + Variables generales)
  // Resultado Neto = Margen Operativo - Impuestos
  const margenOperativo = beneficioBrutoAplicado + margenServicios - gastosFijos - gastosVariables;
  const margenNeto = margenOperativo - impuestosComisiones;

  // % Rentabilidad neta = Resultado Neto / Ingresos totales (ventas de producto
  // + ingresos por servicios). Mide qué porción de la facturación total queda
  // como ganancia final.
  const baseRentabilidad = ventasAplicado + otrosIngresosAplicado;
  const rentabilidadNetaPct = baseRentabilidad > 0
    ? (margenNeto / baseRentabilidad) * 100
    : 0;

  // Punto de equilibrio — fórmula única (utils/puntoEquilibrio.js), la misma
  // que Dashboard y Reportes:
  //   costos variables = CMV + gastos de servicios + gastos "Variable" + comisiones
  //   % margen de contribución = (ingresos − costos variables) / ingresos
  //   punto de equilibrio = gastos fijos / % margen de contribución
  // Antes dividía los fijos por el margen BRUTO (sin restar variables ni
  // comisiones) y daba una meta más baja que la real.
  const cmvAplicado = ventasAplicado - beneficioBrutoAplicado;
  const gastosVariablesEnEquilibrio = peIncluyeVariables ? gastosVariables : 0;
  const costosVariablesTotales = cmvAplicado + gastosServiciosAplicado + gastosVariablesEnEquilibrio + impuestosComisiones;
  const equilibrio = calcularPuntoEquilibrio({
    ingresos: baseRentabilidad,
    costosVariables: costosVariablesTotales,
    gastosFijos,
  });
  const puntoEquilibrioVentas = equilibrio.puntoEquilibrio ?? 0;

  // --- COMPONENTES DE VISTA ---

  const ViewProducts = () => (
    <div className="animate-in fade-in slide-in-from-left-4 duration-300">
        {/* KPI Principal - Mantenemos dentro de ViewProducts por estructura, pero visualmente consistente */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
                <div className="p-3 bg-blue-50 text-[#51A7FD] rounded-xl shadow-inner"><DollarSign size={20} /></div>
                <div>
                    <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Ventas Totales</p>
                    <h3 className="text-lg font-black text-slate-800 tracking-tighter">$ {money(displayedKpis.ventas)}</h3>
                    <p className="text-[10px] font-bold text-slate-500">Facturación bruta</p>
                </div>
            </div>

            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
                <div className="p-3 bg-red-50 text-[#F05A57] rounded-xl shadow-inner"><XCircle size={20} /></div>
                <div>
                    <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Costo Mercadería</p>
                    <h3 className="text-lg font-black text-slate-800 tracking-tighter">$ {money(displayedKpis.cmv)}</h3>
                    <p className="text-[10px] font-bold text-slate-500">CMV calculado</p>
                </div>
            </div>

            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
                <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl shadow-inner"><TrendingUp size={20} /></div>
                <div>
                    <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Beneficio Bruto</p>
                    <h3 className="text-lg font-black text-slate-800 tracking-tighter">$ {money(displayedKpis.beneficioBruto)}</h3>
                    <p className="text-[10px] font-bold text-slate-500">Ventas - Costos</p>
                </div>
            </div>

            <div className="bg-[#51A7FD] p-4 rounded-2xl shadow-lg flex items-center gap-4 text-white">
                <div className="p-3 bg-white/20 rounded-xl"><PieChart size={20} /></div>
                <div>
                    <p className="text-[8px] font-black uppercase tracking-widest opacity-80 leading-none mb-1">Margen %</p>
                    <h3 className="text-lg font-black tracking-tighter">{Number(displayedKpis.margenBrutoPct || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</h3>
                    <p className="text-[10px] font-bold opacity-90">Rentabilidad bruta</p>
                </div>
            </div>
        </div>

        {/* LISTADO DETALLADO */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-4">
            <div className="overflow-x-auto imp-table-wrap">
            <table className="w-full text-left">
                <thead>
                <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                    <SortableTh label="PRODUCTO" sortKey="nombre" />
                    <SortableTh label="MARCA" sortKey="brand" />
                    <SortableTh label="CANT. VENDIDA" sortKey="cant" align="center" />
                    <SortableTh label="PRECIO PROM." sortKey="precioProm" align="right" />
                    <SortableTh label="COSTO UNIT." sortKey="costoUnit" align="right" />
                    <SortableTh label="VENTA TOTAL" sortKey="venta" align="right" />
                    <SortableTh label="COSTO TOTAL" sortKey="cmv" align="right" />
                    <SortableTh label="BENEFICIO" sortKey="beneficio" align="right" />
                    <SortableTh label="MARGEN %" sortKey="margen" align="center" />
                </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                {filteredDetalles.map((d, i) => (
                    <tr key={d.product_id || i} className="hover:bg-blue-50/20 transition-all group">
                    <td className="px-6 py-3.5">
                        <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 border border-slate-200 shrink-0"><Package size={14} /></div>
                        <span className="text-xs font-black text-slate-700 tracking-tight">{d.nombre}</span>
                        </div>
                    </td>
                    <td className="px-6 py-3.5">
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                          {normalizeBrandLabel(d.brand)}
                        </span>
                    </td>
                    <td className="px-6 py-3.5 text-center"><span className="text-[10px] font-black text-slate-600 bg-slate-50 px-2.5 py-1 rounded-md border border-slate-100">{Number(d.cant || 0).toLocaleString('es-AR')}</span></td>
                    <td className="px-6 py-3.5 text-right font-bold text-slate-800 text-xs">$ {money(d.precioProm)}</td>
                    <td className={`px-6 py-3.5 text-right font-bold text-xs ${Number(d.costoUnit || 0) > 0 ? 'text-slate-700' : 'text-rose-600'}`}>$ {money(d.costoUnit)}</td>
                    <td className="px-6 py-3.5 text-right font-black text-slate-900 text-xs">$ {money(d.venta)}</td>
                    <td className={`px-6 py-3.5 text-right font-black text-xs ${Number(d.cmv || 0) > 0 ? 'text-slate-700' : 'text-rose-600'}`}>$ {money(d.cmv)}</td>
                    <td className={`px-6 py-3.5 text-right font-black text-xs ${Number(d.beneficio || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>$ {money(d.beneficio)}</td>
                    <td className="px-6 py-3.5 text-center"><span className={`inline-flex items-center px-3 py-1 rounded-lg text-[10px] font-black uppercase border ${pillClass(d.margen)}`}>{Number(d.margen || 0).toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%</span></td>
                    </tr>
                ))}
                {filteredDetalles.length === 0 && !loading && (
                    <tr><td colSpan="9" className="py-20 text-center opacity-30 italic text-[10px] font-black uppercase tracking-[0.2em]">Sin datos para mostrar</td></tr>
                )}
                </tbody>
            </table>
            </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-4 shadow-sm">
            <Info size={18} className="text-[#51A7FD] shrink-0" />
            <p className="text-[9px] font-black text-slate-500 uppercase leading-relaxed tracking-widest">
                El Costo de Mercadería Vendida (CMV) es el costo total de los productos que vendiste. Se calcula automáticamente multiplicando la cantidad vendida por el costo unitario. El total de ventas que ves arriba muestra lo que realmente se cobró, incluyendo descuentos o recargos aplicados a toda la venta. En cambio, la columna "Venta Total" de la tabla muestra el cálculo por producto: cantidad vendida por precio unitario. Por eso, si usaste descuentos o recargos generales, la suma de esa columna puede no coincidir exactamente con el total cobrado.
            </p>
        </div>
    </div>
  );

  const ResultLine = ({ type, label, total, variant = 'default' }) => {
    const isActive = selectedDetail?.type === type;
    const base = 'w-full flex justify-between items-center text-sm rounded-xl px-2 py-1 -mx-2 transition-all cursor-pointer';
    const hover = isActive ? 'bg-[#51A7FD]/10 ring-1 ring-[#51A7FD]/30' : 'hover:bg-slate-50';
    if (variant === 'minus') {
      return (
        <button type="button" onClick={() => handleSelectDetail(type, label)} className={`${base} ${hover}`}>
          <span className="font-bold text-slate-400 flex items-center gap-2"><span className="text-rose-400">-</span> {label}</span>
          <span className="font-bold text-rose-500">- $ {money(total)}</span>
        </button>
      );
    }
    if (variant === 'tax') {
      return (
        <button type="button" onClick={() => handleSelectDetail(type, label)} className={`${base} ${hover}`}>
          <span className="font-bold text-slate-400 flex items-center gap-2"><span className="text-rose-400">-</span> {label}</span>
          <span className="font-bold text-slate-400">$ {money(total)}</span>
        </button>
      );
    }
    if (variant === 'plus') {
      return (
        <button type="button" onClick={() => handleSelectDetail(type, label)} className={`${base} ${hover}`}>
          <span className="font-bold text-slate-500 flex items-center gap-2"><span className="text-emerald-500">+</span> {label}</span>
          <span className="font-bold text-emerald-600">+ $ {money(total)}</span>
        </button>
      );
    }
    return (
      <button type="button" onClick={() => handleSelectDetail(type, label)} className={`${base} ${hover}`}>
        <span className="font-bold text-slate-600 uppercase tracking-wide">{label}</span>
        <span className="font-black text-slate-900">$ {money(total)}</span>
      </button>
    );
  };

  // Explica, con los números del período, cómo se llega al margen de
  // contribución y al punto de equilibrio. Se abre al hacer click en la
  // tarjeta "Margen de Contribución".
  const pct = (v) => `${Number(v || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`;
  const ExplicacionMargen = ({ wrapBg, accent, accentDim, chipBg }) => {
    const lineas = [
      { signo: '+', label: 'Ventas de productos', monto: ventasAplicado, mostrar: incluirProductos },
      { signo: '+', label: 'Ingresos por servicios', monto: otrosIngresosAplicado, mostrar: incluirServicios },
      { signo: '−', label: 'Costo de la mercadería vendida (CMV)', monto: cmvAplicado, mostrar: incluirProductos },
      { signo: '−', label: 'Gastos por servicios', monto: gastosServiciosAplicado, mostrar: incluirServicios },
      {
        signo: '−',
        label: peIncluyeVariables ? 'Gastos variables' : 'Gastos variables (no se restan)',
        monto: gastosVariablesEnEquilibrio,
        mostrar: true,
        nota: peIncluyeVariables
          ? null
          : `$ ${money(gastosVariables)} cargados en el período quedan afuera: el lógico "Punto de equilibrio: restar gastos variables" está apagado en Empresa → Datos.`,
      },
      { signo: '−', label: 'Comisiones de medios de pago', monto: impuestosComisiones, mostrar: true },
    ].filter((l) => l.mostrar);

    const fila = (izq, label, der, fuerte = false) => (
      <div className={`flex items-center justify-between gap-3 ${chipBg} border rounded-xl px-3 py-2`}>
        <span className={`text-sm font-black ${accent} shrink-0 w-5 text-center`}>{izq}</span>
        <span className={`text-xs flex-1 ${fuerte ? 'font-black text-white' : 'text-white/85'}`}>{label}</span>
        <span className="text-xs font-black text-white shrink-0">{der}</span>
      </div>
    );

    return (
      <div className={`${wrapBg} rounded-3xl shadow-xl p-6 flex flex-col text-white relative overflow-hidden`}>
        <div className="relative z-10 flex items-start justify-between mb-4">
          <div className="min-w-0">
            <p className={`text-[9px] font-black uppercase tracking-[0.22em] ${accent} mb-1`}>Cómo se calcula</p>
            <h3 className="text-xl font-black uppercase tracking-tight">Margen de Contribución</h3>
            <p className={`text-xs mt-2 leading-relaxed ${accentDim}`}>
              Lo que queda de cada peso vendido después de pagar los costos que crecen con la venta.
              Con eso se cubren los gastos fijos; lo que sobra es ganancia.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setSelectedDetail(null)}
            className="text-white/40 hover:text-white transition-colors shrink-0"
            aria-label="Cerrar explicación"
          >
            <XCircle size={20} />
          </button>
        </div>

        <div className="relative z-10 border-t border-white/10 pt-3 space-y-1.5">
          {lineas.map((l) => (
            <div key={l.label}>
              {fila(l.signo, l.label, `$ ${money(l.monto)}`)}
              {l.nota && <p className={`text-[10px] mt-1 mb-1 px-3 leading-snug ${accentDim}`}>{l.nota}</p>}
            </div>
          ))}
          {fila('=', 'Margen de contribución', `$ ${money(equilibrio.margenContribucion)}`, true)}
          {fila('÷', `Ingresos ($ ${money(baseRentabilidad)})`, pct(equilibrio.margenContribucionPct), true)}
        </div>

        <div className="relative z-10 border-t border-white/10 mt-4 pt-3 space-y-1.5">
          <p className={`text-[9px] font-black uppercase tracking-[0.22em] ${accent}`}>Punto de equilibrio</p>
          {equilibrio.estado === 'ok' ? (
            <>
              {fila('', `Gastos fijos $ ${money(gastosFijos)} ÷ ${pct(equilibrio.margenContribucionPct)}`, `$ ${money(equilibrio.puntoEquilibrio)}`, true)}
              <p className={`text-[11px] px-1 leading-relaxed ${accentDim}`}>
                Con este margen, cada $ 100 vendidos dejan $ {money(equilibrio.margenContribucionPct)} para cubrir fijos.
                {equilibrio.cumplida
                  ? ' Las ventas del período ya cubren los gastos fijos.'
                  : ` Faltan $ ${money(equilibrio.faltante)} de ventas para cubrirlos.`}
              </p>
            </>
          ) : (
            <p className={`text-[11px] px-1 leading-relaxed ${accentDim}`}>
              {equilibrio.estado === 'no_alcanzable'
                ? 'Los costos variables son iguales o mayores que los ingresos: cada venta no deja nada para cubrir los gastos fijos, así que no hay un nivel de ventas que llegue al equilibrio. Revisá precios, costos y que no haya compras de mercadería cargadas como gasto variable.'
                : leyendaPuntoEquilibrio(equilibrio.estado)}
            </p>
          )}
        </div>
      </div>
    );
  };

  const DarkDetailPanel = ({ tone = 'navy' }) => {
    const isPurple = tone === 'purple';
    const wrapBg = isPurple ? 'bg-purple-900' : 'bg-[#0B2A36]';
    const accent = isPurple ? 'text-purple-200' : 'text-[#3FF2AA]';
    const accentDim = isPurple ? 'text-purple-200/70' : 'text-[#3FF2AA]/80';
    const chipBg = isPurple ? 'bg-purple-300/10 border-purple-200/15' : 'bg-white/5 border-white/10';

    if (!selectedDetail) {
      return (
        <div className={`${wrapBg} rounded-3xl shadow-xl p-8 flex flex-col items-center justify-center text-center text-white relative overflow-hidden`}>
          <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5 pointer-events-none"></div>
          {isPurple
            ? <Scale size={64} className="text-purple-300 mb-6 opacity-20" />
            : <Layers size={64} className="text-[#3FF2AA] mb-6 opacity-20" />}
          <h2 className="text-3xl font-black uppercase tracking-tight mb-2">
            {isPurple ? 'Equilibrio Financiero' : 'Vision Financiera'}
          </h2>
          <p className={`text-sm ${isPurple ? 'text-purple-200/80' : 'text-white/60'} font-medium max-w-xs leading-relaxed`}>
            Hace click en cualquier linea del panel izquierdo para ver aca el detalle de movimientos.
          </p>
        </div>
      );
    }

    if (selectedDetail.type === 'margenContribucion') {
      return <ExplicacionMargen wrapBg={wrapBg} accent={accent} accentDim={accentDim} chipBg={chipBg} />;
    }

    return (
      <div className={`${wrapBg} rounded-3xl shadow-xl p-6 flex flex-col text-white relative overflow-hidden`}>
        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5 pointer-events-none"></div>
        <div className="relative z-10 flex items-start justify-between mb-4">
          <div className="min-w-0">
            <p className={`text-[9px] font-black uppercase tracking-[0.22em] ${accent} mb-1`}>Detalle de movimientos</p>
            <h3 className="text-xl font-black uppercase tracking-tight truncate">{selectedDetail.label}</h3>
            <p className="text-2xl font-black mt-1">$ {money(currentDetailTotal)}</p>
            <p className={`text-[10px] font-bold uppercase tracking-widest mt-1 ${accentDim}`}>
              {detailRows.length} {detailRows.length === 1 ? 'movimiento' : 'movimientos'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setSelectedDetail(null)}
            className="text-white/40 hover:text-white transition-colors shrink-0"
            aria-label="Cerrar detalle"
          >
            <XCircle size={20} />
          </button>
        </div>
        <div className="relative z-10 border-t border-white/10 pt-3 flex-1 min-h-0">
          {detailRows.length === 0 ? (
            <p className="text-center text-white/40 text-xs italic py-10">Sin movimientos en el periodo seleccionado.</p>
          ) : (
            <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
              {detailRows.map((row, i) => (
                <div
                  key={i}
                  className={`flex items-center justify-between gap-3 ${chipBg} border rounded-xl px-3 py-2`}
                >
                  <span className={`text-[10px] font-black uppercase tracking-widest ${accent} shrink-0 w-16`}>
                    {row.left}
                  </span>
                  <span className="text-xs text-white/85 flex-1 truncate" title={row.mid}>{row.mid}</span>
                  <span className="text-xs font-black text-white shrink-0">{row.right}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const ViewResults = () => (
    <div className="animate-in fade-in slide-in-from-right-4 duration-300 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-3xl border border-slate-200 shadow-xl p-8 flex flex-col justify-center">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-50 text-[#51A7FD] rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-sm"><Calculator size={32} /></div>
              <h3 className="text-2xl font-black text-slate-800 uppercase tracking-tight">Estado de Resultados</h3>
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mt-2">Análisis Financiero del Período</p>
            </div>

            <div className="space-y-3 max-w-md mx-auto w-full">
              {/* BLOQUE PRODUCTOS (solo con lógico de productos activo) */}
              {incluirProductos && (
                <>
                  <ResultLine type="ventas" label="Ventas Totales" total={displayedKpis.ventas} />
                  <div className="border-b border-slate-100 pb-3">
                    <ResultLine type="cmv" label="Costo Mercadería (CMV)" total={displayedKpis.cmv} variant="minus" />
                  </div>

                  <div className="flex justify-between items-center bg-slate-50 p-4 rounded-2xl border border-slate-100">
                    <span className="font-black text-slate-700 text-xs uppercase tracking-widest">Margen Bruto</span>
                    <span className="font-black text-emerald-600 text-lg">$ {money(displayedKpis.beneficioBruto)}</span>
                  </div>
                </>
              )}

              {/* BLOQUE SERVICIOS (solo con lógico de servicios activo y sin filtro) */}
              {incluirServicios && !isFiltered && (
                <>
                  <div className="pt-2">
                    <ResultLine type="otrosIngresos" label="Ingresos por servicios" total={otrosIngresosAplicado} variant="plus" />
                  </div>
                  <div className="border-b border-slate-100 pb-3">
                    <ResultLine type="gastosServicios" label="Gastos por servicios" total={gastosServiciosAplicado} variant="minus" />
                  </div>
                  <div className="flex justify-between items-center bg-slate-50 p-4 rounded-2xl border border-slate-100">
                    <span className="font-black text-slate-700 text-xs uppercase tracking-widest">Margen por servicios</span>
                    <span className={`font-black text-lg ${margenServicios >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>$ {money(margenServicios)}</span>
                  </div>
                </>
              )}

              {/* DESGLOSE GASTOS */}
              <div className="pt-2">
                <ResultLine type="gastosVariables" label="Gastos Variables" total={gastosVariables} variant="minus" />
              </div>
              <ResultLine type="gastosFijos" label="Gastos Fijos" total={gastosFijos} variant="minus" />

              {/* RESULTADO OPERATIVO */}
              <div className="flex justify-between items-center bg-slate-50 p-4 rounded-2xl border border-slate-100 mt-2">
                <span className="font-black text-slate-700 text-xs uppercase tracking-widest">Resultado Operativo</span>
                <span className={`font-black text-lg ${margenOperativo >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>$ {money(margenOperativo)}</span>
              </div>

              <div className="border-b border-slate-100 pb-3 pt-2">
                <ResultLine type="impuestos" label="Impuestos (Comisiones)" total={impuestosComisiones} variant="tax" />
              </div>

              <div className="flex justify-between items-center bg-[#0B2A36] p-5 rounded-2xl shadow-lg text-white mt-2">
                <div className="flex flex-col">
                  <span className="font-black text-xs uppercase tracking-widest">Resultado Neto</span>
                  <span className="text-[9px] font-bold uppercase tracking-widest opacity-70">% Rentabilidad neta</span>
                </div>
                <div className="flex flex-col items-end">
                  <span className={`font-black text-2xl ${margenNeto < 0 ? 'text-rose-300' : 'text-[#3FF2AA]'}`}>$ {money(margenNeto)}</span>
                  <span className={`font-black text-sm ${rentabilidadNetaPct < 0 ? 'text-rose-300' : 'text-[#3FF2AA]'}`}>
                    {baseRentabilidad > 0
                      ? `${rentabilidadNetaPct.toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`
                      : '—'}
                  </span>
                </div>
              </div>
            </div>
        </div>

        <DarkDetailPanel tone="navy" />
    </div>
  );

  const BalanceCard = ({ type, label, value, valueColor = 'text-slate-800' }) => {
    const isActive = selectedDetail?.type === type;
    return (
      <button
        type="button"
        onClick={() => handleSelectDetail(type, label)}
        className={`w-full bg-slate-50 p-5 rounded-2xl border text-center transition-all cursor-pointer
          ${isActive ? 'border-purple-400 ring-2 ring-purple-200 bg-purple-50' : 'border-slate-100 hover:border-purple-300 hover:bg-purple-50/40'}`}
      >
        <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{label}</span>
        <span className={`block text-2xl font-black ${valueColor}`}>{value}</span>
      </button>
    );
  };

  const ViewBalance = () => (
    <div className="animate-in fade-in slide-in-from-right-4 duration-300 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-3xl border border-slate-200 shadow-xl p-8 flex flex-col justify-center">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-purple-50 text-purple-600 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-sm"><Scale size={32} /></div>
              <h3 className="text-2xl font-black text-slate-800 uppercase tracking-tight">Punto de Equilibrio</h3>
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mt-2">Facturación necesaria para cubrir costos</p>
            </div>

            <div className="space-y-6 max-w-md mx-auto w-full">
              <BalanceCard type="gastosFijos" label="Costos Fijos Totales" value={`$ ${money(gastosFijos)}`} />
              <BalanceCard type="gastosVariables" label="Gastos Variables" value={`$ ${money(gastosVariables)}`} />
              <BalanceCard
                type="margenContribucion"
                label="Margen de Contribución"
                value={`${Number(equilibrio.margenContribucionPct || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`}
                valueColor={equilibrio.margenContribucionPct > 0 ? 'text-purple-600' : 'text-rose-600'}
              />
              <div className="border-t border-slate-100 pt-6">
                <p className="text-[10px] text-center font-bold text-slate-400 mb-3 uppercase tracking-widest">VENTAS NECESARIAS (PERÍODO SELECCIONADO)</p>
                <div className="bg-purple-600 p-6 rounded-3xl shadow-lg text-center text-white relative overflow-hidden">
                  <span className="text-3xl font-black block tracking-tight relative z-10">
                    {equilibrio.puntoEquilibrio == null
                      ? (equilibrio.estado === 'no_alcanzable' ? 'No alcanzable' : 'Sin datos')
                      : `$ ${money(puntoEquilibrioVentas)}`}
                  </span>
                  {equilibrio.estado === 'ok' && (
                    <span className="text-[10px] font-bold uppercase tracking-widest block mt-2 relative z-10 opacity-80">
                      {equilibrio.cumplida ? 'Cubierto en el período' : `Faltan $ ${money(equilibrio.faltante)}`}
                    </span>
                  )}
                  {equilibrio.estado !== 'ok' && (
                    <span className="text-[10px] font-bold block mt-2 relative z-10 opacity-80">
                      {leyendaPuntoEquilibrio(equilibrio.estado)}
                    </span>
                  )}
                  <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-purple-500 rounded-full opacity-50 blur-xl"></div>
                </div>
                <p className="text-[9px] text-center text-slate-400 mt-4 italic px-4 font-medium">
                    *Costos fijos ÷ % de margen de contribución. Tocá "Margen de Contribución" para ver el cálculo paso a paso.
                </p>
              </div>
            </div>
        </div>

        <DarkDetailPanel tone="purple" />
    </div>
  );

  const handleGoToDashboard = () => {
    goToFirstSaleOnboardingStep('visit-dashboard');
    navigate('/sistema/dashboard');
  };

  return (
    <div className="imp-page imp-container animate-nova-in">

      {/* HEADER */}
      <div className="imp-header mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <BarChart3 size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">Rentabilidad</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Análisis de ventas, costos y resultados.
            </p>
          </div>
        </div>

        <div className="imp-header-actions">
          {/* Botón Descargar a la Izquierda */}
          <button 
            onClick={exportCSV} 
            className="px-5 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-500 hover:text-[#51A7FD] shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
          >
            <Download size={16} /> Descargar
          </button>

          {/* Navegación de Vistas */}
          {mostrarPorProducto && (
            <button
              onClick={() => setActiveView('products')}
              className={`px-5 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg transition-all
                ${activeView === 'products' ? 'bg-[#3ff2aa] text-[#0B2A36]' : 'bg-[#0B2A36] text-white hover:bg-slate-800'}`}
            >
              <Layers size={16} /> Rentabilidad por producto
            </button>
          )}

          <button 
            onClick={() => setActiveView('results')} 
            data-onboarding-id="rentabilidad-view-results"
            className={`px-5 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg transition-all
                ${activeView === 'results' ? 'bg-[#3ff2aa] text-[#0B2A36]' : 'bg-[#0B2A36] text-white hover:bg-slate-800'}`}
          >
            <Calculator size={16} /> Resultado operativo
          </button>

          <button 
            onClick={() => setActiveView('balance')} 
            className={`px-5 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg transition-all
                ${activeView === 'balance' ? 'bg-[#3ff2aa] text-[#0B2A36]' : 'bg-[#0B2A36] text-white hover:bg-slate-800'}`}
          >
            <Scale size={16} /> Punto de equilibrio
          </button>
        </div>
      </div>

      {firstSaleJourney?.sale ? (
        <div
          data-onboarding-id="rentabilidad-journey-summary"
          className="mb-4 overflow-hidden rounded-[2rem] border border-[#0B2A36]/10 bg-white shadow-sm"
        >
          <div className="bg-[#0B2A36] px-5 py-4 text-white">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[9px] font-black uppercase tracking-[0.22em] text-white/75">
              <Info size={12} />
              Venta guiada
            </div>
            <h2 className="mt-3 text-2xl font-black tracking-tight">
              Asi rindio la venta de {firstSaleJourney.sale.productName}.
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-white/75">
              Este bloque te muestra en simple cuanto entro, cuanto costo y que margen dejo la operacion que acabas de registrar.
            </p>
          </div>

          <div className="p-5">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
                <p className="text-[9px] font-black uppercase tracking-[0.22em] text-slate-400">Ingreso</p>
                <p className="mt-2 text-2xl font-black text-slate-900">$ {money(firstSaleJourney.sale.revenue)}</p>
              </div>
              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
                <p className="text-[9px] font-black uppercase tracking-[0.22em] text-slate-400">Costo</p>
                <p className="mt-2 text-2xl font-black text-slate-700">$ {money(firstSaleJourney.sale.cost)}</p>
              </div>
              <div className="rounded-[1.5rem] border border-emerald-200 bg-emerald-50 p-4">
                <p className="text-[9px] font-black uppercase tracking-[0.22em] text-emerald-600">Beneficio bruto</p>
                <p className="mt-2 text-2xl font-black text-emerald-700">$ {money(firstSaleJourney.sale.profit)}</p>
              </div>
              <div className="rounded-[1.5rem] border border-[#51A7FD]/20 bg-[#F2F9FF] p-4">
                <p className="text-[9px] font-black uppercase tracking-[0.22em] text-[#51A7FD]">Margen bruto</p>
                <p className="mt-2 text-2xl font-black text-[#0B2A36]">{Number(firstSaleJourney.sale.marginPct || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</p>
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-3 rounded-[1.5rem] border border-slate-200 bg-[#F8FAFC] p-4 lg:flex-row lg:items-center lg:justify-between">
              <p className="text-sm leading-relaxed text-slate-600">
                Leiste la rentabilidad de una venta real. El siguiente paso es ir al dashboard para ver el negocio desde una vista mas amplia.
              </p>
              <button
                type="button"
                data-onboarding-id="rentabilidad-journey-dashboard"
                onClick={handleGoToDashboard}
                className="inline-flex items-center justify-center rounded-2xl bg-slate-950 px-5 py-3 text-[10px] font-black uppercase tracking-[0.18em] text-white transition hover:translate-y-[-1px]"
              >
                Ir al dashboard
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* FILTROS GLOBALES: Reorganizado para mejor uso de espacio */}
      <div className="bg-white rounded-2xl border border-[#51A7FD] shadow-sm p-3 mb-4">
        <div className="flex flex-col lg:flex-row items-end lg:items-center gap-3">
          
          {/* Buscador */}
          <div className="relative flex-1 w-full">
            <label className="text-[8px] font-bold text-slate-600 uppercase tracking-widest ml-1 mb-1 block">Buscar Producto</label>
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                <input 
                  value={searchTerm} 
                  onChange={(e) => setSearchTerm(e.target.value)} 
                  placeholder="NOMBRE..." 
                  className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-100 rounded-2xl text-[11px] font-bold outline-none uppercase text-slate-700 focus:border-[#51A7FD]" 
                />
            </div>
          </div>

          {/* Filtro Categorías (Desplegable) */}
          <div className="relative shrink-0" ref={categoryMenuRef}>
             <label className="text-[8px] font-bold text-slate-600 uppercase tracking-widest ml-1 mb-1 block">Categoría</label>
             <button
               onClick={() => setShowCategoryMenu(!showCategoryMenu)}
               className="w-40 px-3 py-2 bg-slate-50 border border-slate-100 rounded-2xl text-[10px] font-bold uppercase text-slate-600 flex items-center justify-between hover:border-[#51A7FD] transition-all"
             >
                <span className="truncate">
                    {selectedCategories.length === 0 ? 'TODAS' : `${selectedCategories.length} SELECCIONADAS`}
                </span>
                <ChevronDown size={14} />
             </button>

             {/* Dropdown Menu */}
             {showCategoryMenu && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white border border-slate-200 rounded-2xl shadow-xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                    <div className="p-2 border-b border-slate-50">
                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest px-2">Filtrar por categoría</p>
                    </div>
                    <div className="max-h-60 overflow-y-auto p-1">
                        {categories.map(cat => {
                            const isSelected = selectedCategories.includes(cat.id);
                            return (
                                <div 
                                    key={cat.id} 
                                    onClick={() => toggleCategory(cat.id)}
                                    className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 rounded-xl cursor-pointer transition-colors"
                                >
                                    {isSelected 
                                        ? <CheckSquare size={14} className="text-[#3FF2AA]" fill="#0B2A36" /> 
                                        : <Square size={14} className="text-slate-300" />
                                    }
                                    <span className={`text-[10px] font-bold uppercase ${isSelected ? 'text-slate-800' : 'text-slate-500'}`}>
                                        {cat.name}
                                    </span>
                                </div>
                            );
                        })}
                        {categories.length === 0 && <p className="px-4 py-3 text-[10px] italic text-slate-300">Sin categorías</p>}
                    </div>
                    {selectedCategories.length > 0 && (
                        <div className="p-2 border-t border-slate-50 bg-slate-50/50">
                            <button 
                                onClick={() => setSelectedCategories([])}
                                className="w-full py-1 text-[9px] font-black text-rose-500 uppercase hover:bg-rose-50 rounded-lg"
                            >
                                Limpiar Filtros
                            </button>
                        </div>
                    )}
                </div>
             )}
          </div>

          <div className="shrink-0 w-full lg:w-44">
            <label className="text-[8px] font-bold text-slate-600 uppercase tracking-widest ml-1 mb-1 block">Marca</label>
            <div className="relative">
              <Tag className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
              <select
                value={brandFilter}
                onChange={(e) => setBrandFilter(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-100 rounded-2xl text-[10px] font-bold uppercase text-slate-600 outline-none focus:border-[#51A7FD]"
              >
                <option value="">TODAS</option>
                {brandOptions.map((brand) => (
                  <option key={brand.value} value={brand.value}>{brand.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Filtro Sucursal (solo si hay más de una) */}
          {isMultiSite && (
            <div className="shrink-0 w-full lg:w-44">
              <label className="text-[8px] font-bold text-slate-600 uppercase tracking-widest ml-1 mb-1 block">Sucursal</label>
              <div className="relative">
                <Store className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                <select
                  value={siteFilter}
                  onChange={(e) => setSiteFilter(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-100 rounded-2xl text-[10px] font-bold uppercase text-slate-600 outline-none focus:border-[#51A7FD]"
                >
                  <option value="">TODAS (CONSOLIDADO)</option>
                  {sites.map((s) => (
                    <option key={s.id} value={s.id}>{s.nombre}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Fechas Compactas */}
          <div className="flex gap-2 shrink-0">
             <div className="relative w-28 lg:w-32">
                <label className="text-[8px] font-bold text-slate-600 uppercase tracking-widest ml-1 mb-1 block">Desde</label>
                <div className="relative">
                    <input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} className="w-full px-2 py-2 bg-slate-50 border border-slate-100 rounded-2xl text-[10px] font-bold outline-none uppercase text-slate-700 focus:border-[#51A7FD]" />
                </div>
             </div>
             <div className="relative w-28 lg:w-32">
                <label className="text-[8px] font-bold text-slate-600 uppercase tracking-widest ml-1 mb-1 block">Hasta</label>
                <div className="relative">
                    <input type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} className="w-full px-2 py-2 bg-slate-50 border border-slate-100 rounded-2xl text-[10px] font-bold outline-none uppercase text-slate-700 focus:border-[#51A7FD]" />
                </div>
             </div>
          </div>
          
          {/* Botón Analizar */}
          <button onClick={analizarPeriodo} disabled={loading} className="shrink-0 px-6 py-[9px] mt-4 lg:mt-0 rounded-2xl bg-[#0B2A36] text-white shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 border border-[#0B2A36] h-[38px]">
            {loading ? <RefreshCcw size={16} className="animate-spin" /> : <TrendingUp size={16} />} Analizar
          </button>

        </div>
      </div>

      {/* CONTENIDO PRINCIPAL SEGÚN VISTA */}
      {activeView === 'products' && <ViewProducts />}
      {activeView === 'results' && <ViewResults />}
      {activeView === 'balance' && <ViewBalance />}

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .imp-table-wrap { width: 100%; overflow-x: auto; }
      `}</style>
    </div>
  );
}

export default RentabilidadVentas;
